import { useState } from 'react';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { Button } from '../components/ui/Button';
import { DataTable, type DataTableColumn } from '../components/ui/DataTable';
import { Icon } from '../components/ui/Icon';
import { PageHeader } from '../components/layout/PageHeader';
import { Modal } from '../components/ui/Modal';
import { SelectField } from '../components/ui/SelectField';
import { StatusPill } from '../components/ui/StatusPill';
import { TextField } from '../components/ui/TextField';
import { SampleNotice } from '../components/feedback/SampleNotice';
import {
  SAMPLE_ANNOUNCEMENTS,
  type SampleAnnouncement,
} from '../features/announcements/sampleAnnouncements';
import { formatCount, formatDateTime } from '../utils/format';
import '../components/ui/ui.css';
import './pages.css';

/**
 * Announcements have no API yet. The screen is a preview: the table is sample
 * data behind `SampleNotice`, and the composer is deliberately inert so nobody
 * can believe a message was sent.
 */
export function Announcements() {
  const { t } = useTranslation('admin');
  const [composerOpen, setComposerOpen] = useState(false);

  const columns: readonly DataTableColumn<SampleAnnouncement>[] = [
    {
      key: 'subject',
      header: t('announcements.columns.subject'),
      render: row => <span className="ui-table__primary">{t(row.subjectKey)}</span>,
    },
    {
      key: 'audience',
      header: t('announcements.columns.audience'),
      render: row => t(row.audienceKey),
    },
    {
      key: 'channel',
      header: t('announcements.columns.channel'),
      render: row => t(row.channelKey),
    },
    {
      key: 'schedule',
      header: t('announcements.columns.schedule'),
      render: row => formatDateTime(row.sendAt),
    },
    {
      key: 'status',
      header: t('announcements.columns.status'),
      render: row => <StatusPill label={t(row.statusKey)} tone="info" />,
    },
    {
      key: 'reach',
      header: t('announcements.columns.reach'),
      numeric: true,
      render: row => formatCount(row.reach),
    },
  ];

  return (
    <div className="page">
      <PageHeader
        title={t('announcements.title')}
        subtitle={t('announcements.subtitle')}
        actions={
          <Button small onClick={() => setComposerOpen(true)}>
            <Icon name="plus" size="sm" />
            {t('announcements.create')}
          </Button>
        }
      />

      <SampleNotice />

      <DataTable
        caption={t('announcements.title')}
        columns={columns}
        rows={SAMPLE_ANNOUNCEMENTS}
        rowKey={row => row.id}
        status="ready"
        emptyTitle={t('announcements.emptyTitle')}
        emptyMessage={t('announcements.emptyMessage')}
      />

      <Modal
        open={composerOpen}
        title={t('announcements.form.title')}
        onClose={() => setComposerOpen(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setComposerOpen(false)}>
              {t('common.close')}
            </Button>
            <Button disabled>{t('announcements.form.submit')}</Button>
          </>
        }>
        <SampleNotice />
        <div className="form-grid">
          <TextField
            label={t('announcements.form.subject')}
            placeholder={t('announcements.form.subjectPlaceholder')}
            defaultValue=""
          />
          <SelectField
            label={t('announcements.form.audience')}
            defaultValue="everyone"
            options={[
              { value: 'everyone', label: t('announcements.audience.everyone') },
              { value: 'active', label: t('announcements.audience.active') },
              { value: 'affiliates', label: t('announcements.audience.affiliates') },
            ]}
          />
          <SelectField
            label={t('announcements.form.channel')}
            defaultValue="push"
            options={[
              { value: 'push', label: t('announcements.channels.push') },
              { value: 'email', label: t('announcements.channels.email') },
              { value: 'inApp', label: t('announcements.channels.inApp') },
            ]}
          />
          <TextField label={t('announcements.form.sendAt')} type="datetime-local" />
        </div>
        <div className="ui-field">
          <label className="ui-field__label" htmlFor="announcement-body">
            {t('announcements.form.body')}
          </label>
          <textarea
            id="announcement-body"
            className="ui-field__control ui-field__control--multiline"
            rows={4}
            placeholder={t('announcements.form.bodyPlaceholder')}
          />
        </div>
        <p className="ui-modal__note">{t('announcements.form.disabledHint')}</p>
      </Modal>
    </div>
  );
}
